import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import i18n from "../../i18n";
import { FaqItem } from "../../types/faq.definition";

interface FaqState {
  faqs: FaqItem[];
  loadedLanguage: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: FaqState = {
  faqs: [],
  loadedLanguage: null,
  loading: false,
  error: null,
};

export const fetchFaqs = createAsyncThunk(
  "faqs/fetchFaqs",
  async (_, { rejectWithValue }) => {
    try {
      const client = (await import("../../config/api.config")).default;
      const lang = i18n.language || "fr";

      const response = await client.apiClient.request({
        method: "GET",
        url: `/faqs`,
        params: { lang },
      });

      return response.data as FaqItem[];
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch FAQs"
      );
    }
  }
);

const faqSlice = createSlice({
  name: "faqs",
  initialState,
  reducers: {
    clearFaqs: state => {
      state.faqs = [];
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchFaqs.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchFaqs.fulfilled,
        (state, action: PayloadAction<FaqItem[]>) => {
          state.loading = false;
          state.faqs = action.payload;
          state.loadedLanguage = i18n.language;
          state.error = null;
        }
      )
      .addCase(fetchFaqs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearFaqs } = faqSlice.actions;

export const selectFaqs = (state: { faqs: FaqState }) => state.faqs.faqs;
export const selectFaqsLoading = (state: { faqs: FaqState }) =>
  state.faqs.loading;
export const selectFaqsError = (state: { faqs: FaqState }) => state.faqs.error;
export const selectFaqsLoadedLanguage = (state: { faqs: FaqState }) =>
  state.faqs.loadedLanguage;

export default faqSlice.reducer;
